import { useEffect, useMemo, useState } from "react";
import api from "@/api/client";
import { toast } from "sonner";
import { wsPipeline } from "@/lib/workspace";
import Header from "@/components/Header";
import BrandFooter from "@/components/BrandFooter";
import CardDetailPanel from "@/components/board/CardDetailPanel";
import { deriveInitials } from "@/lib/pipeline";
import { ownerColor, stageColor } from "@/lib/palette";
import { OverdueSiren } from "@/components/OverdueSiren";
import { AlarmClock, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function OverduePage() {
  const [cards, setCards] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [lists, setLists] = useState({});
  const [owner, setOwner] = useState("all");
  const [selected, setSelected] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [showAck, setShowAck] = useState(false);

  const load = () => {
    Promise.all([
      api.get(`/reports/overdue`, { params: { pipeline: wsPipeline() } }),
      api.get("/templates", { params: { pipeline: wsPipeline() } }),
      api.get("/users"),
      api.get("/lists"),
    ]).then(([o, t, u, l]) => { setCards(o.data); setTemplates(t.data); setUsers(u.data); setLists(l.data); setLoaded(true); });
  };
  useEffect(() => { load(); }, []);

  const tpl = (n) => templates.find((t) => t.stage_number === n);

  const staffOwners = useMemo(() => users.filter((u) => u.role !== "field_agent"), [users]);
  const filtered = useMemo(
    () => (owner === "all" ? cards : cards.filter((c) => c.owner_id === owner)),
    [cards, owner]
  );
  const ackCount = filtered.filter((c) => c.acknowledged_today).length;
  const shown = showAck ? filtered : filtered.filter((c) => !c.acknowledged_today);
  const toggleAck = async (c, e) => {
    e.stopPropagation();
    try {
      await api.post(`/cards/${c.id}/${c.acknowledged_today ? "unacknowledge" : "acknowledge"}`);
      toast.success(c.acknowledged_today ? "Back on the list" : "Acknowledged — handled for today");
      load();
    } catch (err) {
      toast.error("Could not update");
    }
  };

  const today = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  const sevBadge = (over) => {
    const cls = over >= 7 ? "bg-[#b91c1c] text-white" : over >= 3 ? "bg-[#E65100]/15 text-[#E65100]" : "bg-[#FFCB05]/25 text-[#8a6d00]";
    return <span className={`text-xs font-bold px-2 py-0.5 rounded ${cls}`}><AlertTriangle className="inline h-3 w-3 mr-0.5" />{over}d over</span>;
  };

  return (
    <div className="min-h-screen paper-bg">
      <Header />
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-1">
          <AlarmClock className="h-6 w-6 text-[#FBBF24]" />
          <h1 className="text-3xl font-extrabold tracking-tight text-[#15233B]">Overdue leads</h1>
        </div>
        <p className="text-sm text-[#15233B]/60">
          Your morning list — every active lead sitting <span className="font-semibold">past its stage target</span>. {today}.
        </p>

        <div className="mt-4 flex items-center gap-3 mb-4 flex-wrap">
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#15233B]/55">Owner</label>
          <select value={owner} onChange={(e) => setOwner(e.target.value)} data-testid="overdue-owner-filter" className="border border-[#15233B]/15 rounded px-2 py-1.5 text-xs font-semibold bg-white">
            <option value="all">Everyone</option>
            {staffOwners.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <span className="text-xs font-bold text-[#15233B]/60" data-testid="overdue-count">{shown.length} need action</span>
          {ackCount > 0 && (
            <button
              type="button"
              data-testid="overdue-toggle-ack"
              onClick={() => setShowAck((s) => !s)}
              className="text-xs font-semibold text-[#15233B]/60 underline hover:text-[#15233B]"
            >
              {showAck ? "Hide acknowledged" : `Show acknowledged (${ackCount})`}
            </button>
          )}
        </div>

        <div className="bg-white border border-[#15233B]/10 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F1F2F4] text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">
              <tr>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-left px-4 py-3">Stage</th>
                <th className="text-left px-4 py-3">Owner</th>
                <th className="text-left px-4 py-3">Days in stage</th>
                <th className="text-left px-4 py-3">Overdue by</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#15233B]/8">
              {shown.map((c) => {
                const t = tpl(c.current_stage);
                const sc = stageColor(c.current_stage);
                const oc = ownerColor(c.owner_id);
                const ownerUser = users.find((u) => u.id === c.owner_id);
                return (
                  <tr key={c.id} onClick={() => setSelected(c)} data-testid={`overdue-row-${c.av_id}`} className={`hover:bg-[#F6F3EC]/60 cursor-pointer ${c.acknowledged_today ? "opacity-50" : ""}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-[#F1F2F4] grid place-items-center text-[11px] font-bold text-[#15233B]/70">{deriveInitials(c.full_name)}</div>
                        <div><div className="font-bold text-[#15233B] flex items-center gap-1.5">{c.full_name}<OverdueSiren daysOver={c.days_over} className="text-base" /></div><div className="text-[11px] font-mono text-[#15233B]/55">{c.av_id}</div></div>
                      </div>
                    </td>
                    <td className="px-4 py-3"><span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: sc.primary, color: sc.text }}>{String(c.current_stage).padStart(2, "0")} {t?.name}</span></td>
                    <td className="px-4 py-3 text-xs"><span className="inline-flex items-center gap-1.5"><span className="h-5 w-5 rounded-full grid place-items-center text-[9px] font-bold" style={{ backgroundColor: oc.primary, color: oc.text }}>{(ownerUser?.name || "?")[0]}</span>{ownerUser?.name || "—"}</span></td>
                    <td className="px-4 py-3 text-xs text-[#15233B]/70"><span className="font-bold text-[#15233B]">{c.days_in_stage}d</span> <span className="text-[#15233B]/45">/ target {c.target}d</span></td>
                    <td className="px-4 py-3">{sevBadge(c.days_over)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        data-testid={`overdue-ack-${c.av_id}`}
                        onClick={(e) => toggleAck(c, e)}
                        className={`text-xs font-bold px-2.5 py-1 rounded-md border transition-colors ${
                          c.acknowledged_today
                            ? "border-[#166534]/30 text-[#166534] bg-[#166534]/5 hover:bg-[#166534]/10"
                            : "border-[#15233B]/15 text-[#15233B] hover:bg-[#15233B]/5"
                        }`}
                      >
                        {c.acknowledged_today ? "✓ Acknowledged" : "Acknowledge"}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {shown.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm italic text-[#15233B]/50">
                    {loaded ? (
                      ackCount > 0 ? (
                        <><CheckCircle2 className="inline h-5 w-5 mr-1.5 text-[#166534]" />All caught up — {ackCount} acknowledged for today.</>
                      ) : (
                        <><CheckCircle2 className="inline h-5 w-5 mr-1.5 text-[#166534]" />Nothing overdue — every lead is inside its stage target.</>
                      )
                    ) : (
                      "Loading overdue leads…"
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {selected && (
        <CardDetailPanel card={selected} template={tpl(selected.current_stage)} templates={templates} users={users} lists={lists}
          onClose={() => setSelected(null)} onChanged={(u) => { load(); setSelected(u); }} />
      )}
      <BrandFooter />
    </div>
  );
}
